import {CLIssues} from '../issues/issues.types';
import {DateTime} from 'luxon';

enum MediaStatusName {
  Rendered = 'Rendered',
  Completed = 'Completed',
  Pending = 'Pending',
  Failed = 'Failed',
  SyncingWithPlatform = 'SyncingWithPlatform',
  Canceled = 'Canceled',
  PendingToDeletion = 'PendingToDeletion',
  Total = 'Total',
}

enum MediaFileType {
  MP4 = 0,
  WEBM = 1,
  CWF = 6,
  CTS = 7,
}

export enum VideoColorSpace {
  yuv420p = 0, // default for web
  yuv422p = 1, // CTV, Hulu and other high quality
  yuv444p = 2,
  yuv420p10le = 3,
  yuv422p10le = 4,
  yuv444p10le = 5,
}

enum MediaStatus {
  All = -1,
  Pending = 0,
  Rendered = 10,
  Failed = 20,
  SyncingWithPlatform = 30,
  Canceled = 40,
  PendingToDeletion = 50,
  PendingApproval = 100,
  Completed = 200,
}

enum ShortMediaType {
  Video = 'Video',
  Image = 'Image',
}

//TODO_Makho: check where it is used, and if it is safe to remove
enum MediaType {
  VideoAds = 10,
  FacebookVideo = 30,
  FbAdAccount = 35,
  FbAdAccountImage = 40,
  FbAdImage = 45,
  YT = 50,
  VideoCreativePreview = 60,
  VideoTemplatePreview = 70,
  TiktokAdVideo = 80,
  TiktokAdImage = 85,
  GoogleAdsVideo = 90,
  ExternalVideoAds = 100,
  ExternalImageAds = 105,
  ProductVideo = 110,
}

interface MediaContentItem {
  feedId: number;
  itemId: number;
}

interface RemoteClinchMedia {
  id: number;
  status: MediaStatus;
  transcodingStatuses: Record<
    TranscodingPartner,
    {status: TranscodingStatus; errorMessage?: string}
  >;
  type: number;
  fileUrl: string;
  name: string;
  previewUrl?: string;
  posterUrl: string;
  duration?: number;
  clientStyleId: number;
  bizId?: string;
  feedId?: number;
  creativeId?: number;
  locationFeedId?: number;
  content: MediaContentItem[];
  itemListType?: number;
  mainItemIds?: Array<string>;
  feedRelatedItemsLogicId?: string;
  otherItemIds?: Array<string>;
  categoryId?: string;
  issues?: CLIssues;
  extMediaId?: string;
  isVideoType?: boolean;
  isImageType?: boolean;
  createTime: string;
  updateTime: string;
  renderTime: string;
  lastEnqueueTime: string;
  onCompleteTime: string;
  adSize: string;
  outputs: {fileKey: string; fileType?: MediaFileType; videoFormat?: VideoFormat}[];
}

interface ClinchMedia
  extends Omit<
    RemoteClinchMedia,
    'outputs' | 'createTime' | 'updateTime' | 'renderTime' | 'lastEnqueueTime' | 'onCompleteTime'
  > {
  outputs: {fileKey: string; fileType: MediaFileType; videoFormat?: VideoFormat}[];
  createTime: DateTime;
  updateTime: DateTime;
  renderTime: DateTime;
  lastEnqueueTime: DateTime;
  onCompleteTime: DateTime;
}

export enum TranscodingPartner {
  Google = 1021,
}

export const TranscodingPartnerName: Record<TranscodingPartner, string> = {
  [TranscodingPartner.Google]: 'Google',
};

export enum TranscodingStatus {
  Pending = 10,
  Failed = 20,
  Success = 30,
}

export const TranscodingStatusName: Record<TranscodingStatus, string> = {
  [TranscodingStatus.Pending]: 'Pending',
  [TranscodingStatus.Failed]: 'Failed',
  [TranscodingStatus.Success]: 'Success',
};

interface SocialPlatformMedia {
  id: string;
  usedInAdFor: string;
  title?: string;
  description?: string;
  fileUrl: string;
  thumbnailUrl: string;
  posterUrl?: string;
  duration?: number;
  videoPoster?: SocialPlatformMedia;
}

interface RemoteMediaStatusResponse {
  status: MediaStatus;
  id: number;
}

interface MediaStatusResponse extends Omit<RemoteMediaStatusResponse, 'status'> {
  status: MediaStatus;
}

enum CLMimeType {
  JPG_SHORT = 'image/jpg',
  JPG = 'image/jpeg',
  APNG = 'image/apng',
  PNG = 'image/png',
  WEBP = 'image/webp',
  PDF = 'application/pdf',
  CSV = 'text/csv',
  SVG = 'image/svg+xml',
  GIF = 'image/gif',
  MP4 = 'video/mp4',
  WEBM = 'video/webm',
  MOV = 'video/quicktime',
  ZIP = 'application/zip',
  TSV = 'text/tab-separated-values',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  XML = 'application/xml',
  MP3 = 'audio/mp3',
  WAV = 'audio/wav',
  FLACC = 'audio/x-flac',
  OGG = 'audio/ogg',
  M4A = 'audio/mp4',
  TTF = 'font/ttf',
  OTF = 'font/otf',
  WOFF = 'font/woff',
  WOFF2 = 'font/woff2',
  TTC = 'font/collection',
  PSD = 'image/vnd.adobe.photoshop',
  TEXT_XML = 'text/xml',
  SRT = 'application/x-subrip',
}

interface MediaAssetDimensions {
  w: number;
  h: number;
  unit?: string;
}

interface ClinchMediaFilterProps {
  mediaType?: MediaType;
  mediaIds?: number[];
  campaignId?: number;
  campaignVersionId?: number;
  isCTVCampaign?: boolean;
}

export type VideoFormat = {
  fileType: MediaFileType;
  width?: number;
  height?: number;
  videoBitrate?: number;
  audioBitrate?: number;
  forceVideoCbr?: boolean;
  colorSpace?: VideoColorSpace;
  audioAverageLoudness?: number;
  audioMaxPeak?: number;
};

export interface VideoMetadataSummary {
  Duration: number;
  VideoBitrate?: number;
  VideoCodec: string;
  AudioBitrate?: number;
  AudioCodec: string;
}

export {
  MediaStatusResponse,
  ShortMediaType,
  SocialPlatformMedia,
  RemoteClinchMedia,
  ClinchMedia,
  RemoteMediaStatusResponse,
  CLMimeType,
  MediaAssetDimensions,
  MediaStatus,
  MediaType,
  MediaStatusName,
  MediaFileType,
  ClinchMediaFilterProps,
};
